import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitEntry, fileToBase64 } from '../../../services/faceOfBelvoirService';

const BANNER_IMAGE = 'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';
const ENTRIES_CLOSED = true;

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

// ---------------------------------------------------------------------------
// Hero copy section
// ---------------------------------------------------------------------------

const HeroSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem 3.5rem;
  margin-bottom: 2.5rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  border-top: 4px solid ${props => props.theme.colors.accent};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle at top right, rgba(157, 109, 73, 0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.75rem;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 0.75rem;
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.8rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  line-height: 1.15;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TitleDivider = styled.div`
  width: 56px;
  height: 3px;
  background: ${props => props.theme.colors.accent};
  border-radius: 2px;
  margin: 1rem 0 1.5rem;
`;

const Intro = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.75;
  margin: 0 0 1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// ---------------------------------------------------------------------------
// Prize box
// ---------------------------------------------------------------------------

const PrizeBox = styled.div`
  background: linear-gradient(135deg, rgba(16, 29, 64, 0.04) 0%, rgba(157, 109, 73, 0.06) 100%);
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 14px;
  padding: 1.75rem 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }
`;

const PrizeTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PrizeText = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;
  margin: 0;
`;

// ---------------------------------------------------------------------------
// Form section
// ---------------------------------------------------------------------------

const FormCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const FormTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.75rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
`;

const FormSubtitle = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0 0 2rem;
`;

const FieldGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-family: ${props => props.theme.fonts.body};
  font-weight: 700;
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
`;

const Required = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: 3px;
`;

const FieldHint = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.5;
`;

const inputStyles = props => `
  width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 1rem;
  border: 2px solid rgba(16, 29, 64, 0.12);
  border-radius: 10px;
  font-family: ${props.theme.fonts.body};
  font-size: 0.97rem;
  color: ${props.theme.colors.primary};
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;

  &::placeholder {
    color: rgba(16, 29, 64, 0.35);
  }

  &:focus {
    border-color: ${props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Input = styled.input`${inputStyles}`;
const Textarea = styled.textarea`
  ${inputStyles}
  min-height: 110px;
  resize: vertical;
  line-height: 1.6;
`;

// ---------------------------------------------------------------------------
// Photo upload
// ---------------------------------------------------------------------------

const PhotoUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile ? props.theme.colors.accent : 'rgba(16, 29, 64, 0.2)'};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  background: ${props => props.hasFile ? 'rgba(157, 109, 73, 0.04)' : 'rgba(16, 29, 64, 0.02)'};
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    background: rgba(157, 109, 73, 0.04);
  }

  input {
    display: none;
  }
`;

const PhotoPreview = styled.img`
  max-width: 100%;
  max-height: 240px;
  border-radius: 10px;
  object-fit: cover;
  display: block;
  margin: 0 auto 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const PhotoIcon = styled.div`
  margin-bottom: 0.75rem;
  color: rgba(16, 29, 64, 0.35);
`;

const PhotoLabel = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;

const PhotoFilename = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin: 0.5rem 0 0;
`;

// ---------------------------------------------------------------------------
// Consent checkbox
// ---------------------------------------------------------------------------

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1.25rem 1.5rem;
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  background: rgba(16, 29, 64, 0.02);
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    background: rgba(157, 109, 73, 0.04);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  accent-color: ${props => props.theme.colors.accent};
  cursor: pointer;
`;

const CheckboxText = styled.span`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.55;
`;

// ---------------------------------------------------------------------------
// Submit button / feedback
// ---------------------------------------------------------------------------

const SubmitButton = styled.button`
  width: 100%;
  padding: 1.1rem 2rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 12px;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 2rem;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 29, 64, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  font-weight: 600;
  background: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#d1ecf1'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#0c5460'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#bee5eb'};
  line-height: 1.6;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    display: block;
  }
`;

const SuccessTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
`;

const SuccessText = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.05rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;
  max-width: 520px;
  margin: 0 auto;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16, 29, 64, 0.1);
  margin: 2rem 0;
`;

const ClosedBanner = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #1a2d5c 100%);
  color: white;
  border-radius: 14px;
  padding: 1.5rem 2rem;
  margin-bottom: 2.5rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(16, 29, 64, 0.18);

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }
`;

const ClosedBannerTitle = styled.p`
  margin: 0 0 0.5rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.02em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ClosedBannerText = styled.p`
  margin: 0;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FaceOfBelvoir = () => {
  const blockPickerRef = useRef(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    familyInfo: '',
    availability: '',
    marketingConsent: false,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhotoChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Some browsers fire a follow-up click on the label when the dialog closes.
    blockPickerRef.current = true;
    window.setTimeout(() => {
      blockPickerRef.current = false;
    }, 400);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!photoFile) {
      setError('Please attach a family photo before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const photoData = await fileToBase64(photoFile);
      await submitEntry({
        ...form,
        photoData,
        photoFileName: photoFile.name,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        image={BANNER_IMAGE}
        title="Be the Face of Belvoir"
        subtitle="Competition Entry"
      />

      <ContentContainer>
        {ENTRIES_CLOSED && (
          <ClosedBanner>
            <ClosedBannerTitle>New entries now closed</ClosedBannerTitle>
            <ClosedBannerText>
              Thank you to everyone who entered the Be the Face of Belvoir competition.
              We are no longer accepting new submissions.
            </ClosedBannerText>
          </ClosedBanner>
        )}

        {submitted ? (
          <SuccessCard>
            <SuccessIcon>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </SuccessIcon>
            <SuccessTitle>Entry Received</SuccessTitle>
            <SuccessText>
              Thank you for entering the Be the Face of Belvoir competition. We've received your
              entry and will be in touch if your family is selected to take part. Good luck!
            </SuccessText>
            <StatusMessage type="success" style={{ marginTop: '2rem', textAlign: 'left' }}>
              A confirmation email has been sent to <strong>{form.email}</strong>.
            </StatusMessage>
          </SuccessCard>
        ) : (
          <>
            {/* Hero copy */}
            <HeroSection>
              <Eyebrow>{ENTRIES_CLOSED ? 'Competition' : 'Open Competition'}</Eyebrow>
              <PageTitle>Be the Face of Belvoir</PageTitle>
              <TitleDivider />
              <Intro>
                Could your family be the face of one of England's most iconic castles?
              </Intro>
              <Intro>
                We're looking for a real family to star in Belvoir Castle's brand new promotional
                video and marketing campaign, and we want you to be part of it.
              </Intro>
              <Intro style={{ marginBottom: 0 }}>
                Whether you're regulars who know every corner of the estate or you've always dreamed
                of visiting, this is your chance to step into the spotlight and show the world what
                Belvoir means to families like yours.
              </Intro>

              <PrizeBox>
                <PrizeTitle>What's Up for Grabs</PrizeTitle>
                <PrizeText>
                  One lucky family will win the chance to feature in Belvoir's new promotional video
                  and appear across our marketing materials — seen by thousands of visitors,
                  followers, and fans of the castle. On top of that, you'll receive a{' '}
                  <strong>full year's membership to Belvoir Castle</strong>, giving your family
                  unlimited access to the castle and gardens for twelve months.
                </PrizeText>
              </PrizeBox>
            </HeroSection>

            {/* Form */}
            {!ENTRIES_CLOSED && (
            <FormCard>
              <FormTitle>How to Enter</FormTitle>
              <FormSubtitle>
                Simply fill in the form below for your chance to be considered. Good luck!
              </FormSubtitle>

              <form onSubmit={handleSubmit} noValidate>
                {/* Name */}
                <FieldGroup>
                  <Label htmlFor="fob-name">
                    Your Name <Required>*</Required>
                  </Label>
                  <Input
                    id="fob-name"
                    name="name"
                    type="text"
                    placeholder="Full name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </FieldGroup>

                {/* Email */}
                <FieldGroup>
                  <Label htmlFor="fob-email">
                    Your Email Address <Required>*</Required>
                  </Label>
                  <Input
                    id="fob-email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </FieldGroup>

                {/* Phone */}
                <FieldGroup>
                  <Label htmlFor="fob-phone">
                    Your Contact Number <Required>*</Required>
                  </Label>
                  <Input
                    id="fob-phone"
                    name="phone"
                    type="tel"
                    placeholder="e.g. 07700 900 123"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </FieldGroup>

                {/* Address */}
                <FieldGroup>
                  <Label htmlFor="fob-address">
                    Your Address <Required>*</Required>
                  </Label>
                  <Textarea
                    id="fob-address"
                    name="address"
                    placeholder="House number, street, town, postcode"
                    value={form.address}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    style={{ minHeight: '90px' }}
                  />
                </FieldGroup>

                <Divider />

                {/* Family info */}
                <FieldGroup>
                  <Label htmlFor="fob-family">
                    About Your Family <Required>*</Required>
                  </Label>
                  <FieldHint>
                    How many members of your family are there? Please include any children's ages.
                  </FieldHint>
                  <Textarea
                    id="fob-family"
                    name="familyInfo"
                    placeholder="e.g. 2 adults, 3 children (ages 4, 7 and 10)"
                    value={form.familyInfo}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    style={{ marginTop: '0.5rem' }}
                  />
                </FieldGroup>

                {/* Availability */}
                <FieldGroup>
                  <Label htmlFor="fob-availability">
                    Availability for Sunday 28th June <Required>*</Required>
                  </Label>
                  <FieldHint>
                    The shoot will take place on Sunday 28th June. Please confirm whether
                    your family is available on this date, and note any constraints we should
                    be aware of.
                  </FieldHint>
                  <Textarea
                    id="fob-availability"
                    name="availability"
                    placeholder="e.g. Yes, we're available all day on Sunday 28th June."
                    value={form.availability}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    style={{ marginTop: '0.5rem' }}
                  />
                </FieldGroup>

                <Divider />

                {/* Photo upload */}
                <FieldGroup>
                  <Label>
                    Family Photo <Required>*</Required>
                  </Label>
                  <FieldHint style={{ marginBottom: '0.75rem' }}>
                    Please attach a recent photo of your whole family. Accepted formats: JPG, PNG,
                    WEBP (max 10MB).
                  </FieldHint>
                  <PhotoUploadArea
                    hasFile={!!photoFile}
                    onClick={e => {
                      if (blockPickerRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handlePhotoChange}
                      disabled={submitting}
                    />
                    {photoPreview ? (
                      <>
                        <PhotoPreview src={photoPreview} alt="Family photo preview" />
                        <PhotoFilename>{photoFile?.name}</PhotoFilename>
                        <PhotoLabel style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}>
                          Click to change photo
                        </PhotoLabel>
                      </>
                    ) : (
                      <>
                        <PhotoIcon>
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </PhotoIcon>
                        <PhotoLabel>
                          <strong>Click to upload</strong> your family photo
                        </PhotoLabel>
                        <PhotoLabel style={{ fontSize: '0.82rem', marginTop: '0.35rem' }}>
                          JPG, PNG or WEBP — max 10MB
                        </PhotoLabel>
                      </>
                    )}
                  </PhotoUploadArea>
                </FieldGroup>

                <Divider />

                {/* Marketing consent */}
                <FieldGroup style={{ marginBottom: '0.5rem' }}>
                  <CheckboxRow>
                    <Checkbox
                      type="checkbox"
                      name="marketingConsent"
                      checked={form.marketingConsent}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                    <CheckboxText>
                      I'm happy to receive marketing emails from Belvoir Castle about upcoming events,
                      offers, and news. You can unsubscribe at any time.
                    </CheckboxText>
                  </CheckboxRow>
                </FieldGroup>

                {error && (
                  <StatusMessage type="error">{error}</StatusMessage>
                )}

                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? 'Submitting your entry…' : 'Submit My Entry'}
                </SubmitButton>

                <FieldHint style={{ textAlign: 'center', marginTop: '1rem' }}>
                  By entering, you agree to Belvoir Castle using your entry details to consider your
                  family for the promotional campaign. Your details will not be shared with third
                  parties.
                </FieldHint>
              </form>
            </FormCard>
            )}
          </>
        )}
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default FaceOfBelvoir;
